import { QUIZ_ROUTES } from "./quizRoutes";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AlertPopup from "./AlertPopup";
import {
  buildQuizBackTarget,
  buildQuizFlowState,
  persistQuizFlowState,
} from "./quizFlowState";
import "./StartQuizHeader.css";
import qbankImg from "../../assets/images/im4.png";
import QuizPurchaseDialog from "./QuizPurchaseDialog";
import { isPaidQuizLocked } from "./quizAccess";

function resolveUnavailableMessage(quizData = {}) {
  return (
    quizData.quizAttemptMessage ||
    quizData.categoryAttemptMessage ||
    quizData.questionMessage ||
    quizData.dateMessage ||
    quizData.reattemptMessage ||
    "This quiz is not available to start right now."
  );
}

function formatStartQuizData(quizData = {}) {
  return {
    stateName: quizData.stateName || "",
    categoryName: quizData.categoryName || "",
    title: quizData.quizName || quizData.title || "Untitled Quiz",
    questions: quizData.totalQuestions || quizData.questions || 0,
    attemptsUsed:
      quizData.attemptsUsed ??
      Number(quizData.pendingQuizInfo?.attempted_count || 0) ??
      quizData.attemptsCount ??
      0,
    freeAttemptsLeft: quizData.quizAttemptsLeft ?? quizData.freeAttemptsLeft ?? 0,
    type:
      quizData.freePaid === "F"
        ? "Free"
        : quizData.paymentStatus === 1
        ? "Paid"
        : quizData.plan || "Paid",
    duration:
      Number(quizData.duration || 0) > 0
        ? `${Number(quizData.duration || 0)} Minutes`
        : "Duration unavailable",
    marks: quizData.totalMarks || quizData.marks || 0,
    userAttempts: quizData.totalAttempts || quizData.userAttempts || 0,
    languages: quizData.availableLanguages || quizData.language || "English",
    attemptedQuestions:
      Number(quizData.pendingQuizInfo?.attempted_count || 0) ||
      Number(quizData.attemptedQuestions || 0),
    startDate: quizData.startDate || quizData.date || "-",
    endDate: quizData.endDate || quizData.date || "-",
    description:
      quizData.quizDescription ||
      quizData.description ||
      "Quiz details are available once this screen is connected to live data.",
    image: quizData.bannerImage || quizData.image || qbankImg,
    progress:
      Number(quizData.questionCount || quizData.totalQuestions || 0) > 0
        ? Math.round(
            (Number(quizData.pendingQuizInfo?.attempted_count || 0) /
              Number(quizData.questionCount || quizData.totalQuestions || 0)) *
              100
          )
        : Number(quizData.progress || 0),
    canStart: Boolean(quizData.canStart),
    pendingQuizAction: quizData.pendingQuizInfo?.quiz_action || "",
    canUnlockWithAdCoins: Boolean(quizData.canUnlockWithAdCoins),
    coinUnlockCost: Number(quizData.coinUnlockCost || 0),
    unavailableMessage: resolveUnavailableMessage(quizData),
  };
}

const StartQuizHeader = ({ quizData, isLoading = false, navigationState = {}, onBeforeStart }) => {
  const navigate = useNavigate();
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const starting = useRef(false);
  const [isStarting, setIsStarting] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const descriptionRef = useRef(null);
  const test = formatStartQuizData(quizData);

  useEffect(() => {
    const checkDescription = () => {
      const element = descriptionRef.current;

      if (!element) {
        return;
      }

      const style = window.getComputedStyle(element);
      const lineHeight = parseFloat(style.lineHeight);

      if (!lineHeight) {
        return;
      }

      setHasMore(element.scrollHeight > lineHeight * 2 + 1);
    };

    checkDescription();
    window.addEventListener("resize", checkDescription);

    return () => {
      window.removeEventListener("resize", checkDescription);
    };
  }, [test.description]);

  const progress = Math.min(Math.max(test.progress || 0, 0), 100);
  const questionProgress =
    test.questions > 0 ? Math.round((test.attemptedQuestions / test.questions) * 100) : 0;
  const startButtonLabel = test.pendingQuizAction === "Pending" ? "Continue Quiz" : "Start Quiz";
  const pendingAssessmentBatchId = Number(quizData?.pendingQuizInfo?.assesment_batch_id || 0);
  const pendingQuizMode = String(quizData?.pendingQuizInfo?.quiz_mode || "")
    .trim()
    .toUpperCase();

  if (isLoading) {
    return (
      <div className="mock-test-card">
        <div className="mock-test-content">
          <div className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--title" />
          <div className="dashboard-skeleton dashboard-skeleton--line mt-3" />
          <div className="dashboard-skeleton dashboard-skeleton--line mt-2" />
          <div className="dashboard-skeleton dashboard-skeleton--line mt-2" />
          <div className="dashboard-skeleton dashboard-skeleton--line mt-3" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mock-test-card">
        <div className="mock-test-content">
          <h2 className="mock-test-title">{test.title}</h2>

          <div className="mock-test-stats">
            <div className="mock-stat">
              <i className="bi bi-question-circle" />
              <span>{test.questions} Questions</span>
            </div>
            <div className="mock-stat">
              <i className="bi bi-layers" />
              <span>{test.attemptsUsed} Attempts Used</span>
            </div>
            <div className="mock-stat">
              <i className="bi bi-flag" />
              <span>{test.freeAttemptsLeft} Free Attempts Left</span>
            </div>
            <div className="mock-stat">
              <i className="bi bi-tag-fill" />
              <span>{test.type}</span>
            </div>
            <div className="mock-stat">
              <i className="bi bi-clock" />
              <span>{test.duration}</span>
            </div>
            <div className="mock-stat">
              <i className="bi bi-award" />
              <span>{test.marks} Marks</span>
            </div>
            <div className="mock-stat">
              <i className="bi bi-shuffle" />
              <span>{test.userAttempts} attempts by users</span>
            </div>
            <div className="mock-stat">
              <i className="bi bi-translate" />
              <span>{test.languages}</span>
            </div>
          </div>

          <div className={`mock-test-description-wrapper ${showMore ? "expanded" : ""}`}>
            <p ref={descriptionRef} className="mock-test-description">
              <strong>Description:-</strong> {test.description}
            </p>

            {hasMore ? (
              <button
                type="button"
                className="description-toggle"
                onClick={() => setShowMore((currentValue) => !currentValue)}
              >
                {showMore ? "See Less" : "See More"}
              </button>
            ) : null}
          </div>

          <div className="mock-test-question-progress">
            <div className="question-progress-header">
              <strong>Questions</strong>
              <span>
                {test.attemptedQuestions}/{test.questions}
              </span>
            </div>

            <div className="quizz-progress-section">
              <div className="quizz-progress-line">
                <div className="quizz-progress-value" style={{ width: `${questionProgress}%` }} />
              </div>
              <span className="quizz-progress-percent">{progress}%</span>
            </div>
          </div>

          <div className="mock-test-bottom">
            <div className="mock-test-start">
              <span>Start:</span>
              <strong>{test.startDate}</strong>
            </div>

            <div className="mock-test-start">
              <span>End:</span>
              <strong>{test.endDate}</strong>
            </div>
          </div>
        </div>

        <div className="mock-test-right">
          <img src={test.image} alt={test.title} className="mock-test-image" />

          <button
            type="button"
            className="start-quiz-btn"
            disabled={isStarting}
            onClick={async () => {
              if (starting.current) return;
              starting.current = true;
              setIsStarting(true);
              try {
              if (onBeforeStart && !(await onBeforeStart())) return;
              if (isPaidQuizLocked(quizData)) {
                setShowPurchaseModal(true);
                return;
              }
              if (!test.canStart) {
                setShowAlertModal(true);
                return;
              }

              const currentFlowState = buildQuizFlowState({
                ...navigationState,
                quizData,
              });

              if (test.pendingQuizAction === "Pending" && pendingAssessmentBatchId > 0) {
                const pendingFlowState = buildQuizFlowState({
                  ...currentFlowState,
                  assesment_batch_id: pendingAssessmentBatchId,
                  resume_on_open: true,
                  selectedMode: pendingQuizMode
                    ? {
                        id: pendingQuizMode,
                        dataKey: pendingQuizMode,
                        title: "Pending Quiz",
                      }
                    : null,
                  quizBackTarget: buildQuizBackTarget(QUIZ_ROUTES.start, currentFlowState),
                });

                persistQuizFlowState(pendingFlowState);
                navigate(QUIZ_ROUTES.detail, {
                  state: pendingFlowState,
                });
                return;
              }

              const selectionFlowState = buildQuizFlowState({
                ...currentFlowState,
                quizBackTarget: buildQuizBackTarget(QUIZ_ROUTES.start, currentFlowState),
              });

              persistQuizFlowState(selectionFlowState);
              navigate(QUIZ_ROUTES.selection, {
                state: selectionFlowState,
              });
              } finally {
                starting.current = false;
                setIsStarting(false);
              }
            }}
          >
            <i className="bi bi-rocket-takeoff-fill" />
            <span>
              {isPaidQuizLocked(quizData)
                ? "Unlock Quiz"
                : test.canStart
                ? startButtonLabel
                : "Unavailable"}
            </span>
          </button>
        </div>
      </div>

      <QuizPurchaseDialog open={showPurchaseModal} onClose={() => setShowPurchaseModal(false)}
        quiz={quizData} navigationState={navigationState} coinBalance={quizData?.adCoinBalance || 0} />
      <AlertPopup
        open={showAlertModal}
        onClose={() => setShowAlertModal(false)}
        title="Quiz Unavailable"
        message={test.unavailableMessage}
        hideAction
      />
    </>
  );
};

export default StartQuizHeader;
